import { ChevronDownIcon, XCircleIcon } from '@heroicons/react/solid'
import clsx from 'clsx'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Row } from '../layout/row'
import {
  FILTERS,
  FOR_YOU_KEY,
  Filter,
  SearchParams,
  TOPIC_FILTER_KEY,
} from '../search'
import DropdownMenu from '../widgets/dropdown-menu'
import { getLabelFromValue } from './search-dropdown-helpers'
import { LiteGroup } from 'common/group'
import { User } from 'common/user'

// Polymarket-style clean filter pills
export const minimalistIndigoSelectedClass =
  'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
export const unselectedClass =
  'bg-transparent text-ink-500 hover:text-ink-700 hover:bg-ink-100 dark:hover:bg-ink-200'

export function FilterPill(props: {
  selected: boolean
  color?: 'gray' | 'minimalist-indigo'
  disabled?: boolean
  onSelect: () => void
  className?: string
  children: ReactNode
  type?: 'spice' | 'sweepies'
}) {
  const { children, selected, onSelect, className, type, color, disabled } =
    props

  return (
    <button
      disabled={disabled}
      className={clsx(
        // Polymarket-style: minimal, clean pills
        'flex shrink-0 select-none flex-row items-center whitespace-nowrap rounded-full px-3 py-1 text-sm font-medium outline-none transition-all',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        type === 'spice'
          ? selected
            ? 'bg-amber-100 text-amber-700'
            : 'text-ink-500 hover:text-amber-700 hover:bg-amber-50'
          : type === 'sweepies'
          ? selected
            ? 'bg-amber-100 text-amber-700'
            : 'text-ink-500 hover:text-amber-700 hover:bg-amber-50'
          : color === 'gray' && selected
          ? 'bg-ink-200 text-ink-700'
          : color === 'minimalist-indigo' && selected
          ? minimalistIndigoSelectedClass
          : selected
          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
          : unselectedClass,
        className
      )}
      onClick={onSelect}
    >
      {children}
    </button>
  )
}

export function AdditionalFilterPill(props: {
  className?: string
  children: ReactNode
  onXClick: () => void
}) {
  const { children, className, onXClick } = props

  return (
    <Row
      className={clsx(
        'relative select-none items-center gap-1 whitespace-nowrap rounded-full py-0.5 pl-2 pr-1 text-sm outline-none transition-colors',
        'bg-primary-500 text-white',
        className
      )}
    >
      {children}
      <button
        className="text-ink-200 dark:text-ink-800 cursor-pointer rounded-full transition-colors hover:text-white hover:dark:text-white"
        onClick={onXClick}
      >
        <XCircleIcon className="h-4 w-4 " />
      </button>
    </Row>
  )
}

export function FilterDropdownPill(props: {
  selectFilter: (selection: Filter) => void
  currentFilter: Filter
}) {
  const { selectFilter, currentFilter } = props
  // Remove the closing-month filter from the list while it's in its own button
  const currentFilterLabel =
    // currentFilter === 'closing-month' ? getLabelFromValue(FILTERS, 'open') :
    getLabelFromValue(FILTERS, currentFilter)

  return (
    <DropdownMenu
      withinOverflowContainer
      items={FILTERS.map((filter) => {
        return {
          name: filter.label,
          onClick: () => selectFilter(filter.value),
        }
      })}
      buttonContent={(open) => (
        <DropdownPill color={'light-gray'} open={open}>
          {currentFilterLabel}
        </DropdownPill>
      )}
      selectedItemName={currentFilterLabel}
      closeOnClick
    />
  )
}

export function TopicDropdownPill(props: {
  initialTopics: LiteGroup[]
  currentTopicFilter?: string
  user: User | null | undefined
  forYou: boolean
  updateParams: (params: Partial<SearchParams>) => void
}) {
  const { t } = useTranslation()
  const { initialTopics, currentTopicFilter, user, forYou, updateParams } =
    props

  const currentTopicInInitialTopicsName = initialTopics.find(
    (topic) => topic.slug == currentTopicFilter
  )?.name
  const currentTopicLabel = forYou
    ? t('search.yourTopics')
    : currentTopicInInitialTopicsName ?? t('search.allTopics')

  const selectTopicFilter = (selection: string) => {
    if (selection === currentTopicFilter) {
      return
    }

    updateParams({
      [TOPIC_FILTER_KEY]: selection,
      [FOR_YOU_KEY]: '0',
    })
  }

  const forYouItem = user
    ? {
        name: t('search.yourTopics'),
        onClick: () =>
          updateParams({
            [FOR_YOU_KEY]: '1',
            [TOPIC_FILTER_KEY]: '',
          }),
      }
    : null

  const items = [
    ...(forYouItem ? [forYouItem] : []), // Include forYouItem only if it is not null
    {
      name: t('search.allTopics'),
      onClick: () =>
        updateParams({
          [FOR_YOU_KEY]: '0',
          [TOPIC_FILTER_KEY]: '',
        }),
    },
    ...initialTopics.map((topic) => ({
      name: topic.name,
      onClick: () => selectTopicFilter(topic.slug),
    })),
  ]

  return (
    <DropdownMenu
      withinOverflowContainer
      items={items}
      buttonContent={(open) => (
        <DropdownPill color={'indigo'} open={open}>
          {currentTopicLabel}
        </DropdownPill>
      )}
      selectedItemName={currentTopicLabel}
      closeOnClick
      menuItemsClass="max-h-64 overflow-y-auto"
    />
  )
}

export function DropdownPill(props: {
  color?: 'indigo' | 'gray' | 'light-gray'
  open: boolean
  children: ReactNode
  onClick?: () => void
  className?: string
}) {
  const color = props.color ?? 'gray'

  return (
    <div
      className={clsx(
        // Polymarket-style: clean dropdown pills
        'flex cursor-pointer select-none flex-row items-center whitespace-nowrap rounded-full py-1 pl-3 pr-1.5 text-sm font-medium outline-none transition-all',
        color === 'indigo'
          ? 'bg-primary-100 text-primary-700 hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-400'
          : color === 'light-gray'
          ? 'bg-ink-100 text-ink-600 hover:bg-ink-200'
          : 'bg-ink-100 text-ink-600 hover:bg-ink-200',
        props.className
      )}
      onClick={props.onClick}
    >
      {/* eslint-disable react/prop-types */}
      {props.children}
      <ChevronDownIcon
        className={clsx(
          'ml-0.5 h-4 w-4 transition-transform',
          props.open ? 'rotate-180' : ''
        )}
      />
    </div>
  )
}
